export function normalizeInstitutionName(raw) {
  if (raw == null) return '';
  const s = String(raw)
    .toLowerCase()
    .replace(/['']/g, '')
    .trim();
  if (!s) return '';
  return s
    .replace(/[.,;:/&]+/g, ' ')
    .replace(
      /\b(university|universidad|universidade|université|universität|college|colegio|institut[eo]|polytechnic|academy)\b/g,
      ' '
    )
    .replace(/\b(of|the|de|du|del|la|le|les|d|el)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function looseMatch(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  const aTokens = new Set(a.split(' ').filter((t) => t.length >= 3));
  const bTokens = new Set(b.split(' ').filter((t) => t.length >= 3));
  if (aTokens.size === 0 || bTokens.size === 0) return false;
  if (aTokens.size !== bTokens.size) return false;
  for (const t of aTokens) if (!bTokens.has(t)) return false;
  return true;
}

/**
 * Split a free-text education line into candidate institution names. Mirrors the BE
 * (api/src/lib/almaMaterMatch.ts): education is one string listing several degrees, so the
 * whole line never equalled a single university name. Keep the two copies in sync.
 */
function splitEducationText(text) {
  const out = [];
  for (const entry of text.replace(/\([^)]*\)?/g, ' ').split(/[;|\n]+/)) {
    const e = entry.trim();
    if (!e) continue;
    out.push(e);
    for (const part of e.split(/\s*,\s*|\s+[-–—]\s+/)) {
      const p = part.trim();
      if (p && p !== e) out.push(p);
    }
  }
  return out;
}

function collectPersonaInstitutions(persona) {
  const out = [];
  if (!persona) return out;
  const pushEdu = (v) => {
    if (typeof v === 'string' && v.trim()) {
      for (const seg of splitEducationText(v)) out.push({ via: 'education', text: seg });
    } else if (v && typeof v === 'object') {
      const nm = v.institution ?? v.school ?? v.name ?? v.university ?? '';
      if (typeof nm === 'string' && nm.trim()) out.push({ via: 'education', text: nm });
    }
  };
  const edu = persona.education;
  if (Array.isArray(edu)) edu.forEach(pushEdu);
  else pushEdu(edu);
  const accName =
    persona.account_name || persona.account?.account_name || persona.institution || '';
  if (typeof accName === 'string' && accName.trim()) {
    out.push({ via: 'employer', text: accName });
  }
  return out;
}

/**
 * Returns { matched_university, matched_via: 'education'|'employer', matched_against } or null.
 * Education matches preferred over employer (degree > coworker).
 */
export function personaSharesAlmaMater(persona, userUniversities) {
  if (!persona || !Array.isArray(userUniversities) || userUniversities.length === 0) return null;
  const targets = collectPersonaInstitutions(persona).map((t) => ({
    via: t.via,
    norm: normalizeInstitutionName(t.text),
    raw: t.text,
  }));
  if (targets.length === 0) return null;
  for (const order of ['education', 'employer']) {
    for (const u of userUniversities) {
      const nu = normalizeInstitutionName(u?.name || '');
      if (!nu) continue;
      for (const t of targets) {
        if (t.via !== order) continue;
        if (looseMatch(nu, t.norm)) {
          return {
            matched_university: String(u.name || '').trim(),
            matched_via: t.via,
            matched_against: t.raw,
          };
        }
      }
    }
  }
  return null;
}
